import { useMemo, useCallback } from "react";
import { Button, Tooltip, Flex } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import FilterDropdown from "./FilterDropdown";
import { formatFilterLabel } from "./filtersUtil";

interface AppliedFiltersSectionProps {
  filters: any;
  setFilters: any;
  filterOptions: any;
  allFiltersData?: any;
  clearFilters: any;
  columnsTitleCreator?: (column: string) => string;
  hiddenFilterKeys?: string[];
}

const AppliedFiltersSection = ({
  filters,
  setFilters,
  filterOptions,
  allFiltersData,
  clearFilters,
  columnsTitleCreator,
  hiddenFilterKeys = [],
}: AppliedFiltersSectionProps) => {
  const selectedKeyFilters = useMemo(() => {
    return Object.keys(filters || {}).filter((key) => {
      if (hiddenFilterKeys.includes(key)) {
        return false;
      }

      const filterValue = filters[key];

      if (!filterValue || !Array.isArray(filterValue)) {
        return false;
      }

      if (filterValue.length === 0) {
        return false;
      }

      const firstCondition = filterValue[0];
      if (
        firstCondition &&
        typeof firstCondition === "object" &&
        firstCondition.condition
      ) {
        if (
          firstCondition.condition === "has_data" ||
          firstCondition.condition === "no_data"
        ) {
          return true;
        }
      }

      return true;
    });
  }, [filters, hiddenFilterKeys]);

  const handleDeleteFilter = useCallback(
    (filterKey: string) => {
      setFilters({
        ...filters,
        [filterKey]: [],
      });
    },
    [filters, setFilters],
  );

  const handleClearAll = useCallback(() => {
    clearFilters();
  }, [clearFilters]);

  return (
    <Flex wrap gap="small" className="mt-4">
      {selectedKeyFilters.length > 0 && (
        <>
          {selectedKeyFilters.map((filterKey) => {
            const dataType = allFiltersData?.[filterKey]?.data_type || "text";
            const valuesArray =
              filterOptions[filterKey]?.values ||
              allFiltersData?.[filterKey]?.values ||
              [];
            const filterOptionsMemo = valuesArray.map(
              (option: string | boolean | null) => ({
                label: formatFilterLabel(option),
                value: option,
              }),
            );

            return (
              <FilterDropdown
                key={filterKey}
                filterKey={filterKey}
                dataType={dataType}
                filterOptions={filterOptionsMemo}
                onDelete={() => handleDeleteFilter(filterKey)}
                setFilters={setFilters}
                currentFilters={filters}
                showTitle={true}
                columnsTitleCreator={columnsTitleCreator}
              />
            );
          })}
          <Tooltip title="Clear all filters" placement="topRight">
            <Button
              type="text"
              className="p-0"
              data-testid="applied-filters-clear-button"
              icon={<CloseOutlined style={{ fontSize: "12px" }} />}
              onClick={handleClearAll}
            >
              <span>Clear</span>
            </Button>
          </Tooltip>
        </>
      )}
    </Flex>
  );
};

export default AppliedFiltersSection;
